/**
 * Helpers for reasoning about a PDP's "optimal mix" (`ideal_mix_ranked`, the
 * rank-carrying target list served on the PDP and its original snapshot).
 *
 * Pure and dependency-free so they can be shared across packages and
 * unit-tested without rendering React or touching the API layer.
 */

type PortfolioRankedEntry = {
  rank: number;
  asset_type: string;
  target_count: number;
};

/**
 * Normalizes a classification string for comparison (case + non-alphanumeric insensitive).
 * `"Feature/Benefit"`, `"feature_benefit"`, `"Feature-Benefit"` all collapse to `"featurebenefit"`.
 */
function normalizeClassification(value: string | null | undefined): string {
  return (value ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');
}

/**
 * Narrows `ideal_mix_ranked` to the entries that are actually targeted
 * (`target_count > 0`), in rank order. Ranks are never re-based — they feed slot ids.
 *
 * Rank-less rows are dropped before sorting so a non-numeric rank can't scramble
 * the comparator.
 */
function targetedIdealMix(idealMixRanked: readonly PortfolioRankedEntry[] | null | undefined): PortfolioRankedEntry[] {
  if (!idealMixRanked) return [];
  return idealMixRanked
    .filter((entry) => Number.isFinite(entry.rank) && entry.target_count > 0)
    .map((entry) => ({ rank: entry.rank, asset_type: entry.asset_type, target_count: entry.target_count }))
    .sort((a, b) => a.rank - b.rank);
}

/**
 * True when the PDP has a configured optimal mix that does NOT include the
 * given asset classification — i.e. assets of that class are unlikely to score
 * well for this category.
 *
 * Returns `false` when the mix is unconfigured (no targeted entries) or the
 * classification is missing, since in those cases we can't claim the asset is
 * excluded from the mix.
 */
function isClassificationOutsideIdealMix(
  idealMixRanked: readonly PortfolioRankedEntry[] | null | undefined,
  classification: string | null | undefined,
): boolean {
  const normalized = normalizeClassification(classification);
  if (!normalized) return false;

  const ranked = targetedIdealMix(idealMixRanked);
  if (ranked.length === 0) return false;

  return !ranked.some((entry) => normalizeClassification(entry.asset_type) === normalized);
}

export { isClassificationOutsideIdealMix, normalizeClassification, targetedIdealMix };
export type { PortfolioRankedEntry };
